import { UserService } from './../../../core/services/user.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { AfterViewInit, Component, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { MatChipEditedEvent, MatChipInputEvent } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin, takeUntil, Observable } from 'rxjs';
import { NoteTypeService } from '../../../core/services/note-type.service';
import { BaseComponent } from './../../../core/components/base/base.component';
import { FeedbackService } from './../../../core/services/feedback.service';

@Component({
  selector: 'app-note-type-settings',
  templateUrl: './note-type-settings.component.html',
  styleUrls: ['./note-type-settings.component.scss']
})
export class NoteTypeSettingsComponent extends BaseComponent implements OnDestroy {


  @ViewChild('addNoteTypeDialog') addNoteTypeDialog?: TemplateRef<any>;
  @ViewChild('importNoteTypesDialog') importNoteTypesDialog?: TemplateRef<any>;
  @ViewChild('importTemplatesDialog') importTemplatesDialog?: TemplateRef<any>;
  noteTypes: any = null;
  editingNoteType: any = {};

  readonly separatorKeysCodes: number[] = [13, 188]; // Enter and comma key codes
  addOnBlur = true;

  isSupportUser: boolean = false;
  importNoteTypesJson: string = '';
  noteTypeTemplates: any[] = [];
  selectedTemplateIds: string[] = [];

  constructor(
    private userService: UserService,
    private noteTypeService: NoteTypeService,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);
    this.noteTypeService.noteTypes$.subscribe(noteTypes => {
      if (noteTypes) {
        this.noteTypes = this.updateNoteTypesStructure(noteTypes);
      }
    });

    this.noteTypeService.noteTypeTemplates$.pipe(takeUntil(this.destroy$)).subscribe(templates => {
      this.noteTypeTemplates = templates;
    });

    this.userService.userProfile$.subscribe(user => {
      if (user && (user.id === '511f39e1-ff14-4a6d-b35a-6fcc22923b50' || user.id === '8f78c3c3-032e-4d2d-8ab3-d4209bb0a305')) {
        this.isSupportUser = true;
      } else {
        this.isSupportUser = false;
      }
    });
  }

  override ngOnDestroy(): void {
    this.noteTypeService.getNoteTypesByGroup()
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  private updateNoteTypesStructure(noteTypes: any[]): any[] {
    // normalize headings
    for (let nt of noteTypes) {
      if (this.noteTypeService.areHeadingsStrings(nt.headings)) {
        nt.headings = this.noteTypeService.convertHeadingsToObjects(nt.headings);
      }
    }
    // sort in-place by the label
    return noteTypes.sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
    );
  }

  addHeading(noteType: any, event: MatChipInputEvent, autoSave: boolean = true): void {
    const value = (event.value || '').trim();

    if (value) {
      noteType.headings.push({ text: value, subheadings: [] });
    }

    event.chipInput!.clear();
    if (autoSave) {
      this.saveNoteType(noteType);
    }
  }

  addSubheading(noteType: any, heading: any, event: MatChipInputEvent, autoSave: boolean = true) {
    const value = (event.value || '').trim();
    if (value) {
      heading.subheadings.push({ text: value });
    }
    event.chipInput!.clear();
    if (autoSave) {
      this.saveNoteType(noteType);
    }
  }

  removeHeading(noteType: any, heading: any, autoSave: boolean = true): void {
    const index = noteType.headings.findIndex((h: any) => h.text === heading.text);

    if (index >= 0) {
      noteType.headings.splice(index, 1);
    }
    if (autoSave) {
      this.saveNoteType(noteType);
    }
  }

  removeSubheading(noteType: any, heading: any, subheading: any, autoSave: boolean = true) {
    const index = heading.subheadings.findIndex((s: any) => s.text === subheading.text);
    if (index >= 0) {
      heading.subheadings.splice(index, 1);
    }
    if (autoSave) {
      this.saveNoteType(noteType);
    }
  }

  editHeading(noteType: any, heading: any, event: MatChipEditedEvent, autoSave: boolean = true) {
    const value = event.value.trim();

    if (!value) {
      this.removeHeading(noteType, heading);
      return;
    }

    const index = noteType.headings.findIndex((h: any) => h.text === heading.text);
    if (index >= 0) {
      noteType.headings[index] = { text: value, subheadings: noteType.headings[index].subheadings || [] };
    }
    if (autoSave) {
      this.saveNoteType(noteType);
    }
  }

  editSubheading(noteType: any, heading: any, subheading: any, event: MatChipEditedEvent, autoSave: boolean = true) {
    const value = event.value.trim();

    if (!value) {
      this.removeSubheading(noteType, heading, subheading);
      return;
    }

    const index = heading.subheadings.findIndex((s: any) => s.text === subheading.text);
    if (index >= 0) {
      heading.subheadings[index] = { text: value };
    }
    if (autoSave) {
      this.saveNoteType(noteType);
    }
  }

  openAddNoteTypeDialog(event?: MouseEvent | null, noteType?: any) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.editingNoteType = noteType || { id: this.noteTypes.length + 1, canDelete: true, label: '', headings: [], forAi: true, templateText: '', userPrompt: '', forCura: false, isProcedure: false, isBilling: false };
    
    // Ensure forCura note types have isProcedure and isBilling set to false
    if (this.editingNoteType.forCura) {
      this.editingNoteType.isProcedure = false;
      this.editingNoteType.isBilling = false;
    }
    
    // Ensure billing note types have isProcedure, forAi, and forCura set to false
    if (this.editingNoteType.isBilling) {
      this.editingNoteType.isProcedure = false;
      this.editingNoteType.forAi = false;
      this.editingNoteType.forCura = false;
    }
    
    this.dialog.open(this.addNoteTypeDialog!, { width: '600px' });
  }

  onForAiToggle(event: any): void {
    if (event.checked) {
      if (this.editingNoteType.forCura) {
        this.editingNoteType.forCura = false;
      }
      if (this.editingNoteType.isBilling) {
        this.editingNoteType.isBilling = false;
      }
    }
  }

  onForCuraToggle(event: any): void {
    if (event.checked) {
      if (this.editingNoteType.forAi) {
        this.editingNoteType.forAi = false;
      }
      if (this.editingNoteType.isBilling) {
        this.editingNoteType.isBilling = false;
      }
      // ForCura note types should always have isProcedure and isBilling set to false
      this.editingNoteType.isProcedure = false;
      this.editingNoteType.isBilling = false;
    }
  }

  onIsProcedureToggle(event: any): void {
    if (event.checked && this.editingNoteType.isBilling) {
      this.editingNoteType.isBilling = false;
    }
  }

  onIsBillingToggle(event: any): void {
    if (event.checked) {
      // Billing notes cannot be procedure notes, AI notes, or Cura notes
      if (this.editingNoteType.isProcedure) {
        this.editingNoteType.isProcedure = false;
      }
      if (this.editingNoteType.forAi) {
        this.editingNoteType.forAi = false;
      }
      if (this.editingNoteType.forCura) {
        this.editingNoteType.forCura = false;
      }
    }
  }

  openImportNoteTypesDialog(event?: MouseEvent | null) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.dialog.open(this.importNoteTypesDialog!, { width: '600px' });
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.editingNoteType.headings, event.previousIndex, event.currentIndex);
  }

  dropSubheading(event: CdkDragDrop<any[]>, heading: any) {
    // Move subheadings only within their parent heading
    moveItemInArray(heading.subheadings, event.previousIndex, event.currentIndex);
  }

  saveNoteType(noteType: any) {
    if (typeof noteType.id !== 'string') {
      delete noteType.id;
    }

    const sub = this.noteTypeService.updateNoteTypes([noteType])
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response) => {
          if (!noteType.id) {
            this.noteTypes.push(response.data[0]);
          }
          this.dialog.closeAll();
          this.showToastMessage('Note types updated successfully');
          sub.unsubscribe();
        },
        (error) => {
          console.error('Error updating note tpes:', error);
          this.showToastMessage('There was an error updating note types. Please try again.');
          sub.unsubscribe();
        });
  }

  validateJson() {
    try {
      JSON.parse(this.importNoteTypesJson);
    } catch (error) {
      this.showToastMessage('Invalid JSON. Please check your JSON and try again.');
    }
  }

  async importNoteTypes() {
    try {
      const parsed = JSON.parse(this.importNoteTypesJson);
      
      // Normalize to array: if it's a single object, wrap it in an array
      const json = Array.isArray(parsed) ? parsed : [parsed];

      if (json.length === 0) {
        this.showToastMessage('No note types to import');
        return;
      }

      const validNoteTypes = json.filter((item: any) => item.label && item.headings && Array.isArray(item.headings) && item.headings.length > 0);
      if (validNoteTypes.length === 0) {
        this.showToastMessage('No valid note types to import');
        return;
      }

      this.showToastMessage('Importing note types...');

      const importRequests = [];
      for (let item of json) {
        delete item.id;
        if (item.userPrompt) {
          delete item.userPrompt;
          this.showToastMessage('User prompt is not supported in the import. It will be ignored.');
        }
        // check for existing note type with the same label
        const existingNoteType = this.noteTypes.find((nt: any) => nt.label === item.label);
        if (existingNoteType) {
          const confirmAdd = await this.showConfirmationDialog(undefined, 'Note type with the same title already exists. Do you want to add it anyway?', [], 'Ignore', 'Add Anyway', false);
          if (confirmAdd) {
            importRequests.push(this.noteTypeService.updateNoteTypes([item]));
          } else {
            continue;
          }
        } else {
          importRequests.push(this.noteTypeService.updateNoteTypes([item]));
        }
      }

      if (importRequests.length === 0) {
        this.showToastMessage('No valid note types to import');
        return;
      }

      const sub =forkJoin(importRequests)
        .pipe(takeUntil(this.destroy$))
        .subscribe(results => {
          for (let result of results) {
            this.noteTypes.push(result.data[0]);
          }
          this.showToastMessage('Note types imported successfully');
          this.dialog.closeAll();
          sub.unsubscribe();
        });

      this.showToastMessage('Note types imported successfully');
      this.dialog.closeAll();

    } catch (error) {
      // failed to parse JSON
      this.showToastMessage('Failed to parse JSON. Please check your JSON and try again.');
    }

  }

  openImportTemplatesDialog(event?: MouseEvent | null) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.selectedTemplateIds = [];
    const dialogRef = this.dialog.open(this.importTemplatesDialog!, { width: '600px' });
    
    // Subscribe to selection changes
    dialogRef.afterOpened().subscribe(() => {
      // Get the selection list from the dialog
      const dialogElement = document.querySelector('mat-selection-list');
      if (dialogElement) {
        // Listen for selection changes
        setTimeout(() => {
          const selectionList = dialogElement as any;
          if (selectionList && selectionList.selectedOptions) {
            selectionList.selectedOptions.changed.subscribe((change: any) => {
              this.selectedTemplateIds = change.source.selected.map((opt: any) => opt.value);
            });
          }
        }, 100);
      }
    });
  }

  onTemplateSelectionChange(event: any) {
    this.selectedTemplateIds = event.source.selectedOptions.selected.map((opt: any) => opt.value);
  }

  async importSelectedTemplates() {
    if (this.selectedTemplateIds.length === 0) {
      this.showToastMessage('Please select at least one template to import');
      return;
    }

    const selectedTemplates = this.noteTypeTemplates.filter(t => this.selectedTemplateIds.includes(t.id));
    
    // Check for existing note types with the same label
    const duplicateLabels: string[] = [];
    
    for (const template of selectedTemplates) {
      const existingNoteType = this.noteTypes.find((nt: any) => 
        nt.label && nt.label.toLowerCase() === template.label.toLowerCase()
      );
      if (existingNoteType) {
        duplicateLabels.push(template.label);
      }
    }

    if (duplicateLabels.length > 0) {
      const duplicateMessage = duplicateLabels.length === 1
        ? `A note type with the name "${duplicateLabels[0]}" already exists.`
        : `Note types with the following names already exist: ${duplicateLabels.join(', ')}.`;
      
      const confirmed = await this.showConfirmationDialog(
        undefined,
        `${duplicateMessage} Do you want to import anyway?`,
        [],
        'Cancel',
        'Import Anyway',
        false
      );
      
      if (!confirmed) {
        return;
      }
    }

    const importRequests: Observable<any>[] = [];

    for (const template of selectedTemplates) {
      // Clone template data and prepare for import
      const clonedTemplate: any = {
        label: template.label,
        headings: Array.isArray(template.headings) ? JSON.parse(JSON.stringify(template.headings)) : [],
        templateText: template.templateText || '',
        isProcedure: template.isProcedure || false,
        isBilling: template.isBilling || false,
        forAi: template.forAi !== undefined ? template.forAi : true,
        forCura: template.forCura || false,
        userPrompt: template.userPrompt || '',
        canDelete: true,
        active: true
      };

      importRequests.push(this.noteTypeService.updateNoteTypes([clonedTemplate]));
    }

    if (importRequests.length === 0) {
      this.showToastMessage('No templates selected');
      return;
    }

    const sub = forkJoin(importRequests)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (results) => {
          for (let result of results) {
            if (result && result.data && result.data[0]) {
              this.noteTypes.push(result.data[0]);
            }
          }
          this.showToastMessage(`Successfully imported ${importRequests.length} note type template(s)`);
          this.dialog.closeAll();
          this.selectedTemplateIds = [];
          sub.unsubscribe();
        },
        (error) => {
          console.error('Error importing templates:', error);
          this.showToastMessage('Error importing templates. Please try again.');
          sub.unsubscribe();
        }
      );
  }

  async deleteNoteType(id: string | number) {
    const confirmed = await this.showConfirmationDialog();

    if (typeof id === 'number') {
      this.noteTypes = this.noteTypes.filter((cb: any) => cb.id !== id);
      return;
    }

    if (confirmed) {
      const sub = this.noteTypeService.deleteNoteType(id)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (response: any) => {
            this.noteTypes = this.noteTypes.filter((cb: any) => cb.id !== id);
            this.showToastMessage('Note type deleted successfully');
            sub.unsubscribe();
          },
          (error) => {
            console.error('Error deleting note type:', error);
            this.showToastMessage('There was an error deleting the note type. Please try again.');
            sub.unsubscribe();
          }
        );
    }
  }
}